import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import Icon from '../components/Icon';

const MEAL_PRESETS = ['Breakfast', 'Mid-Morning Snack', 'Lunch', 'Afternoon Snack', 'Dinner', 'Evening Snack'];
const UNITS = ['g', 'ml', 'oz', 'serving', 'cup', 'tbsp', 'tsp', 'piece', 'slice'];
const TIMING_WINDOWS = ['general', 'pre-workout', 'post-workout', 'morning', 'evening'];

const DIETARY_RESTRICTIONS = [
  'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Keto',
  'Paleo', 'Low-FODMAP', 'Halal', 'Kosher', 'Low-Sodium',
  'Low-Carb', 'High-Protein', 'Mediterranean', 'Whole30',
];
const ALLERGENS = [
  'Peanuts', 'Tree Nuts', 'Milk / Dairy', 'Eggs', 'Wheat / Gluten',
  'Soy', 'Fish', 'Shellfish', 'Sesame', 'Mustard', 'Sulphites',
];

const MACRO_PRESETS = {
  balanced:    { label: 'Balanced 40/30/30',     p: 0.30, c: 0.40, f: 0.30 },
  endurance:   { label: 'Endurance 50/25/25',    p: 0.25, c: 0.50, f: 0.25 },
  keto:        { label: 'Keto 5/30/65',          p: 0.30, c: 0.05, f: 0.65 },
  highprotein: { label: 'High Protein 35/40/25', p: 0.40, c: 0.35, f: 0.25 },
  lowcarb:     { label: 'Low Carb 20/35/45',     p: 0.35, c: 0.20, f: 0.45 },
};

function calcMacros(calories, preset) {
  const r = MACRO_PRESETS[preset];
  if (!r || !calories) return null;
  return {
    daily_protein_g: Math.round((calories * r.p) / 4),
    daily_carbs_g:   Math.round((calories * r.c) / 4),
    daily_fat_g:     Math.round((calories * r.f) / 9),
  };
}

function ChipGroup({ options, selected, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      {options.map(opt => {
        const active = selected.includes(opt);
        return (
          <button key={opt} type="button" onClick={() => onChange(active ? selected.filter(v => v !== opt) : [...selected, opt])}
            style={{ padding: '6px 14px', borderRadius: 20,
              border: active ? '1.5px solid var(--color-primary)' : '1.5px solid var(--color-border)',
              background: active ? 'rgba(215,38,56,0.12)' : 'var(--color-surface-2)',
              color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer', transition: 'all 0.15s' }}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const USDA_CATEGORIES = [
  { id: 'beef',        label: 'Beef & Veal' },
  { id: 'poultry',     label: 'Chicken & Poultry' },
  { id: 'fish',        label: 'Fish & Seafood' },
  { id: 'pork',        label: 'Pork & Lamb' },
  { id: 'eggs',        label: 'Eggs' },
  { id: 'dairy',       label: 'Dairy & Cheese' },
  { id: 'vegetables',  label: 'Vegetables' },
  { id: 'fruits',      label: 'Fruits' },
  { id: 'legumes',     label: 'Legumes & Beans' },
  { id: 'grains',      label: 'Grains & Rice' },
  { id: 'pasta',       label: 'Pasta & Noodles' },
  { id: 'bread',       label: 'Bread & Wraps' },
  { id: 'nuts',        label: 'Nuts & Seeds' },
  { id: 'oils',        label: 'Oils & Fats' },
  { id: 'snacks',      label: 'Snacks & Bars' },
  { id: 'cereals',     label: 'Breakfast Cereals' },
  { id: 'beverages',   label: 'Beverages' },
  { id: 'sauces',      label: 'Sauces & Condiments' },
  { id: 'supplements', label: 'Supplements & Powders' },
  { id: 'fastfood',    label: 'Fast Food' },
];

function USDAFoodPicker({ onSelect }) {
  const [category,  setCategory]  = useState('');
  const [foods,     setFoods]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [foodId,    setFoodId]    = useState('');

  async function handleCategoryChange(e) {
    const cat = e.target.value;
    setCategory(cat);
    setFoodId('');
    setFoods([]);
    if (!cat) return;
    setLoading(true);
    try {
      const data = await api.get('/nutrition/usda/by-category?category=' + cat);
      setFoods(data || []);
    } catch {}
    setLoading(false);
  }

  function handleFoodChange(e) {
    const id = e.target.value;
    setFoodId(id);
    if (!id) return;
    const item = foods.find(f => String(f.fdc_id) === id);
    if (item) {
      onSelect(item);
      setTimeout(() => { setCategory(''); setFoods([]); setFoodId(''); }, 100);
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
      <div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>Category</div>
        <select className="form-select" value={category} onChange={handleCategoryChange}
          style={{ minHeight: 38, fontSize: 13 }}>
          <option value="">— Select category —</option>
          {USDA_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
          Food {loading ? '(loading...)' : foods.length ? `(${foods.length} found)` : ''}
        </div>
        <select className="form-select" value={foodId} onChange={handleFoodChange}
          disabled={!category || loading} style={{ minHeight: 38, fontSize: 13 }}>
          <option value="">— Select food —</option>
          {foods.map(f => (
            <option key={f.fdc_id} value={f.fdc_id}>
              {f.food_name}{f.calories_per_100g != null ? ' (' + Math.round(f.calories_per_100g) + ' kcal/100g)' : ''}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function FoodRow({ food, onChange, onRemove }) {
  function handleUSDASelect(item) {
    const qty = Number(food.quantity) || 100;
    const scale = qty / 100;
    onChange({
      ...food,
      food_name:  item.food_name,
      usda_fdc_id: item.fdc_id,
      calories:   item.calories_per_100g != null ? Math.round(item.calories_per_100g * scale) : food.calories,
      protein_g:  item.protein_per_100g  != null ? (item.protein_per_100g  * scale).toFixed(1) : food.protein_g,
      carbs_g:    item.carbs_per_100g    != null ? (item.carbs_per_100g    * scale).toFixed(1) : food.carbs_g,
      fat_g:      item.fat_per_100g      != null ? (item.fat_per_100g      * scale).toFixed(1) : food.fat_g,
      sugar_g:    item.sugar_per_100g    != null ? (item.sugar_per_100g    * scale).toFixed(1) : food.sugar_g,
      fibre_g:    item.fibre_per_100g    != null ? (item.fibre_per_100g    * scale).toFixed(1) : food.fibre_g,
      sodium_mg:  item.sodium_per_100g   != null ? (item.sodium_per_100g   * scale).toFixed(1) : food.sodium_mg,
    });
  }

  return (
    <div style={{ background: 'var(--color-surface-2)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <input className="form-input" style={{ flex: 1, marginRight: 8 }}
          placeholder="Food name (e.g. Chicken Breast)"
          value={food.food_name || ''}
          onChange={e => onChange({ ...food, food_name: e.target.value })} />
        <button className="icon-btn" onClick={onRemove}><Icon name="icon-delete.png" alt="Remove" /></button>
      </div>
      <USDAFoodPicker onSelect={handleUSDASelect} />
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>Quantity</div>
          <input className="form-input" type="number" min="0" step="0.1" placeholder="100"
            value={food.quantity || ''} onChange={e => onChange({ ...food, quantity: e.target.value })}
            style={{ minHeight: 40, fontSize: 14 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>Unit</div>
          <select className="form-select" value={food.unit || 'g'} onChange={e => onChange({ ...food, unit: e.target.value })}
            style={{ minHeight: 40, fontSize: 14 }}>
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
        {[['Kcal', 'calories'], ['Protein (g)', 'protein_g'], ['Carbs (g)', 'carbs_g'], ['Fat (g)', 'fat_g']].map(([label, key]) => (
          <div key={key}>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>{label}</div>
            <input className="form-input" type="number" min="0" step="0.1" placeholder="0"
              value={food[key] || ''} onChange={e => onChange({ ...food, [key]: e.target.value })}
              style={{ minHeight: 40, fontSize: 13 }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
        {[['Fibre (g)', 'fibre_g'], ['Sodium (mg)', 'sodium_mg'], ['Sugar (g)', 'sugar_g']].map(([label, key]) => (
          <div key={key}>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>{label}</div>
            <input className="form-input" type="number" min="0" step="0.1" placeholder="0"
              value={food[key] || ''} onChange={e => onChange({ ...food, [key]: e.target.value })}
              style={{ minHeight: 36, fontSize: 13 }} />
          </div>
        ))}
      </div>
      <input className="form-input" placeholder="Notes (optional)" value={food.notes || ''}
        onChange={e => onChange({ ...food, notes: e.target.value })} style={{ minHeight: 40, fontSize: 14 }} />
    </div>
  );
}

export default function NutritionPlanBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== 'new';

  const [clients,         setClients]         = useState([]);
  const [saving,          setSaving]          = useState(false);
  const [showAssign,      setShowAssign]      = useState(false);
  const [assignClientId,  setAssignClientId]  = useState('');
  const [assignStartDate, setAssignStartDate] = useState('');
  const [assigned,        setAssigned]        = useState(false);
  const [activeTab,       setActiveTab]       = useState('plan');
  const [weeks,           setWeeks]           = useState([]);
  const [weeksSaving,     setWeeksSaving]     = useState(false);
  const [journal,         setJournal]         = useState([]);

  const [form, setForm] = useState({
    name: '', goal: '', daily_calories: '', daily_protein_g: '',
    daily_carbs_g: '', daily_fat_g: '', duration_weeks: '', notes: '',
    macro_split_preset: '', daily_fibre_g: '', daily_sodium_mg: '', daily_sugar_g: '',
  });

  const [dietaryRestrictions, setDietaryRestrictions] = useState([]);
  const [allergies,           setAllergies]           = useState([]);

  const [meals, setMeals] = useState([
    { meal_order: 1, meal_name: 'Breakfast', timing_window: 'morning',   foods: [] },
    { meal_order: 2, meal_name: 'Lunch',     timing_window: 'general',   foods: [] },
    { meal_order: 3, meal_name: 'Dinner',    timing_window: 'evening',   foods: [] },
  ]);

  useEffect(() => {
    api.get('/clients').then(setClients);
    if (isEdit) {
      api.get('/nutrition/' + id).then(data => {
        setForm({
          name:               data.name               || '',
          goal:               data.goal               || '',
          daily_calories:     data.daily_calories     || '',
          daily_protein_g:    data.daily_protein_g    || '',
          daily_carbs_g:      data.daily_carbs_g      || '',
          daily_fat_g:        data.daily_fat_g        || '',
          duration_weeks:     data.duration_weeks     || '',
          notes:              data.notes              || '',
          macro_split_preset: data.macro_split_preset || '',
          daily_fibre_g:      data.daily_fibre_g      || '',
          daily_sodium_mg:    data.daily_sodium_mg    || '',
          daily_sugar_g:      data.daily_sugar_g      || '',
        });
        setDietaryRestrictions(data.dietary_restrictions || []);
        setAllergies(data.allergies || []);
        setMeals((data.meals || []).map(m => ({
          meal_order:   m.meal_order,
          meal_name:    m.meal_name,
          timing_window: m.timing_window || 'general',
          foods: (m.foods || []).map(f => ({
            food_name:  f.food_name,
            quantity:   f.quantity,
            unit:       f.unit || 'g',
            calories:   f.calories,
            protein_g:  f.protein_g,
            carbs_g:    f.carbs_g,
            fat_g:      f.fat_g,
            fibre_g:    f.fibre_g || '',
            sodium_mg:  f.sodium_mg || '',
            sugar_g:    f.sugar_g || '',
            usda_fdc_id: f.usda_fdc_id || null,
            notes:      f.notes || '',
          }))
        })));
        setWeeks(data.weeks || []);
      });
      api.get('/nutrition/' + id + '/journal').then(setJournal).catch(() => {});
    }
  }, [id, isEdit]);

  // Auto-calc macros when preset or calories change
  function handlePresetChange(preset) {
    setForm(f => {
      const cal = Number(f.daily_calories);
      const calc = calcMacros(cal, preset);
      return {
        ...f,
        macro_split_preset: preset,
        ...(calc ? { daily_protein_g: calc.daily_protein_g, daily_carbs_g: calc.daily_carbs_g, daily_fat_g: calc.daily_fat_g } : {})
      };
    });
  }

  function handleCaloriesChange(cal) {
    setForm(f => {
      const calc = f.macro_split_preset ? calcMacros(Number(cal), f.macro_split_preset) : null;
      return {
        ...f,
        daily_calories: cal,
        ...(calc ? { daily_protein_g: calc.daily_protein_g, daily_carbs_g: calc.daily_carbs_g, daily_fat_g: calc.daily_fat_g } : {})
      };
    });
  }

  function initWeeks() {
    const dw = Number(form.duration_weeks);
    if (!dw) return;
    const arr = [];
    for (let i = 1; i <= dw; i++) {
      const existing = weeks.find(w => w.week_number === i);
      arr.push(existing || { week_number: i, daily_calories: form.daily_calories, protein_g: form.daily_protein_g, carbs_g: form.daily_carbs_g, fat_g: form.daily_fat_g, notes: '' });
    }
    setWeeks(arr);
  }

  async function handleSaveWeeks() {
    setWeeksSaving(true);
    try { await api.put('/nutrition/' + id + '/weeks', { weeks }); }
    catch (err) { alert(err.message); }
    setWeeksSaving(false);
  }

  function addMeal() {
    const next = meals.length + 1;
    setMeals(prev => [...prev, { meal_order: next, meal_name: 'Meal ' + next, timing_window: 'general', foods: [] }]);
  }
  function removeMeal(mi) {
    setMeals(prev => prev.filter((_, i) => i !== mi).map((m, i) => ({ ...m, meal_order: i + 1 })));
  }
  function updateMealName(mi, name) {
    setMeals(prev => prev.map((m, i) => i === mi ? { ...m, meal_name: name } : m));
  }
  function updateMealTiming(mi, timing_window) {
    setMeals(prev => prev.map((m, i) => i === mi ? { ...m, timing_window } : m));
  }
  function addFood(mi) {
    setMeals(prev => prev.map((m, i) => i === mi
      ? { ...m, foods: [...m.foods, { food_name: '', quantity: '', unit: 'g', calories: '', protein_g: '', carbs_g: '', fat_g: '', fibre_g: '', sodium_mg: '', sugar_g: '', usda_fdc_id: null, notes: '' }] }
      : m));
  }
  function updateFood(mi, fi, updated) {
    setMeals(prev => prev.map((m, i) => i === mi
      ? { ...m, foods: m.foods.map((f, j) => j === fi ? updated : f) } : m));
  }
  function removeFood(mi, fi) {
    setMeals(prev => prev.map((m, i) => i === mi
      ? { ...m, foods: m.foods.filter((_, j) => j !== fi) } : m));
  }

  const totals = meals.reduce((acc, m) => {
    m.foods.forEach(f => {
      acc.calories  += Number(f.calories  || 0);
      acc.protein_g += Number(f.protein_g || 0);
      acc.carbs_g   += Number(f.carbs_g   || 0);
      acc.fat_g     += Number(f.fat_g     || 0);
      acc.fibre_g   += Number(f.fibre_g   || 0);
      acc.sodium_mg += Number(f.sodium_mg || 0);
      acc.sugar_g   += Number(f.sugar_g   || 0);
    });
    return acc;
  }, { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fibre_g: 0, sodium_mg: 0, sugar_g: 0 });

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, meals, dietary_restrictions: dietaryRestrictions, allergies };
      if (isEdit) {
        await api.put('/nutrition/' + id, payload);
        navigate('/nutrition/' + id);
      } else {
        const created = await api.post('/nutrition', { ...form, dietary_restrictions: dietaryRestrictions, allergies });
        await api.put('/nutrition/' + created.id, payload);
        navigate('/nutrition/' + created.id);
      }
    } catch (err) {
      alert(err.message);
      setSaving(false);
    }
  }

  async function handleAssign(e) {
    e.preventDefault();
    try {
      await api.post('/nutrition/' + id + '/assign', {
        client_id: parseInt(assignClientId),
        start_date: assignStartDate || null,
      });
      setAssigned(true);
      setShowAssign(false);
    } catch (err) {
      alert(err.message);
    }
  }

  const TABS = isEdit ? ['plan', 'weeks', 'journal'] : ['plan'];

  return (
    <div>
      <div className="page-header">
        <button className="icon-btn" onClick={() => navigate('/nutrition')}>
          <Icon name="icon-back.png" alt="Back" />
        </button>
        <h1 style={{ flex: 1 }}>{isEdit ? 'Edit Plan' : 'New Nutrition Plan'}</h1>
        {isEdit && (
          <button className="icon-btn" onClick={() => setShowAssign(true)} title="Assign to client">
            <Icon name="icon-user.png" alt="Assign" />
          </button>
        )}
      </div>

      {isEdit && (
        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ flex: 1, padding: '10px 0', background: 'transparent', border: 'none',
                borderBottom: activeTab === t ? '2px solid var(--color-primary)' : '2px solid transparent',
                color: activeTab === t ? 'var(--color-primary)' : 'var(--color-text-muted)',
                fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
                textTransform: 'capitalize' }}>
              {t === 'plan' ? 'Plan Builder' : t === 'weeks' ? 'Week Cycling' : 'Adherence Journal'}
            </button>
          ))}
        </div>
      )}

      <div className="page-content">
        {assigned && <div className="success-msg">Nutrition plan assigned successfully!</div>}

        {showAssign && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Assign to Client</div>
            <form onSubmit={handleAssign}>
              <div className="form-group">
                <label className="form-label">Client</label>
                <select className="form-select" value={assignClientId} onChange={e => setAssignClientId(e.target.value)} required>
                  <option value="">Select client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date (optional)</label>
                <input className="form-input" type="date" value={assignStartDate} onChange={e => setAssignStartDate(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" style={{ flex: 1 }}>Assign</button>
                <button className="btn btn-ghost" type="button" style={{ flex: 1 }} onClick={() => setShowAssign(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'plan' && (
          <form onSubmit={handleSave}>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Plan Details</div>
              <div className="form-group">
                <label className="form-label">Plan Name *</label>
                <input className="form-input" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Goal</label>
                <input className="form-input" value={form.goal}
                  onChange={e => setForm(f => ({ ...f, goal: e.target.value }))}
                  placeholder="e.g. Weight loss, Muscle gain, Maintenance" />
              </div>
              <div className="form-group">
                <label className="form-label">Duration (weeks)</label>
                <input className="form-input" type="number" min="1" value={form.duration_weeks}
                  onChange={e => setForm(f => ({ ...f, duration_weeks: e.target.value }))} />
              </div>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Dietary Restrictions</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 2 }}>Select all that apply</div>
              <ChipGroup options={DIETARY_RESTRICTIONS} selected={dietaryRestrictions} onChange={setDietaryRestrictions} />
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Allergies & Intolerances</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 2 }}>Flag ingredients to avoid</div>
              <ChipGroup options={ALLERGENS} selected={allergies} onChange={setAllergies} />
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Daily Targets</div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 6 }}>Macro Split Preset — auto-calculates from calories</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(MACRO_PRESETS).map(([key, val]) => {
                    const active = form.macro_split_preset === key;
                    return (
                      <button key={key} type="button" onClick={() => handlePresetChange(active ? '' : key)}
                        style={{ padding: '6px 12px', borderRadius: 20,
                          border: active ? '1.5px solid var(--color-primary)' : '1.5px solid var(--color-border)',
                          background: active ? 'rgba(215,38,56,0.12)' : 'var(--color-surface-2)',
                          color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
                          fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer' }}>
                        {val.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <div className="form-group">
                  <label className="form-label">Daily Calories (kcal)</label>
                  <input className="form-input" type="number" min="0" value={form.daily_calories}
                    onChange={e => handleCaloriesChange(e.target.value)} />
                </div>
                {[['Protein (g)', 'daily_protein_g'], ['Carbohydrates (g)', 'daily_carbs_g'], ['Fat (g)', 'daily_fat_g']].map(([label, key]) => (
                  <div className="form-group" key={key}>
                    <label className="form-label">{label}</label>
                    <input className="form-input" type="number" min="0" value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '8px 0 6px' }}>Micronutrient Targets</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 12px' }}>
                {[['Fibre (g)', 'daily_fibre_g'], ['Sodium (mg)', 'daily_sodium_mg'], ['Sugar (g)', 'daily_sugar_g']].map(([label, key]) => (
                  <div className="form-group" key={key}>
                    <label className="form-label">{label}</label>
                    <input className="form-input" type="number" min="0" value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                  </div>
                ))}
              </div>

              {(totals.calories > 0 || totals.protein_g > 0) && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--color-surface-2)', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6 }}>Totals from food entries</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, marginBottom: 4 }}>
                    <span><strong>{Math.round(totals.calories)}</strong> kcal</span>
                    <span><strong>{totals.protein_g.toFixed(1)}</strong>g protein</span>
                    <span><strong>{totals.carbs_g.toFixed(1)}</strong>g carbs</span>
                    <span><strong>{totals.fat_g.toFixed(1)}</strong>g fat</span>
                  </div>
                  {(totals.fibre_g > 0 || totals.sodium_mg > 0 || totals.sugar_g > 0) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--color-text-muted)' }}>
                      {totals.fibre_g   > 0 && <span>{totals.fibre_g.toFixed(1)}g fibre</span>}
                      {totals.sodium_mg > 0 && <span>{totals.sodium_mg.toFixed(0)}mg sodium</span>}
                      {totals.sugar_g   > 0 && <span>{totals.sugar_g.toFixed(1)}g sugar</span>}
                    </div>
                  )}
                </div>
              )}
            </div>

            {meals.map((meal, mi) => (
              <div key={mi} className="card" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <select className="form-select"
                      value={MEAL_PRESETS.includes(meal.meal_name) ? meal.meal_name : '__custom__'}
                      onChange={e => { if (e.target.value !== '__custom__') updateMealName(mi, e.target.value); }}
                      style={{ marginBottom: 6 }}>
                      {MEAL_PRESETS.map(p => <option key={p} value={p}>{p}</option>)}
                      {!MEAL_PRESETS.includes(meal.meal_name) && <option value="__custom__">{meal.meal_name}</option>}
                    </select>
                    <input className="form-input" value={meal.meal_name}
                      onChange={e => updateMealName(mi, e.target.value)}
                      placeholder="Custom meal name" style={{ fontWeight: 600, marginBottom: 6 }} />
                    <select className="form-select" value={meal.timing_window || 'general'}
                      onChange={e => updateMealTiming(mi, e.target.value)}
                      style={{ fontSize: 13 }}>
                      {TIMING_WINDOWS.map(tw => (
                        <option key={tw} value={tw}>{tw.charAt(0).toUpperCase() + tw.slice(1).replace('-', ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="icon-btn" onClick={() => removeMeal(mi)}>
                    <Icon name="icon-delete.png" alt="Remove" />
                  </button>
                </div>
                {meal.foods.map((food, fi) => (
                  <FoodRow key={fi} food={food}
                    onChange={updated => updateFood(mi, fi, updated)}
                    onRemove={() => removeFood(mi, fi)} />
                ))}
                <button type="button" className="btn btn-ghost" style={{ width: '100%', marginTop: 4 }} onClick={() => addFood(mi)}>
                  <Icon name="icon-add.png" size={16} /> Add Food
                </button>
              </div>
            ))}

            <button type="button" className="btn btn-ghost" style={{ width: '100%', marginBottom: 12 }} onClick={addMeal}>
              <Icon name="icon-add.png" size={16} /> Add Meal
            </button>

            <div className="card" style={{ marginBottom: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Additional Notes</label>
                <textarea className="form-input" rows={3} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Hydration tips, supplement recommendations..." />
              </div>
            </div>

            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Plan'}
            </button>
          </form>
        )}

        {activeTab === 'weeks' && (
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Week-by-Week Calorie & Macro Cycling</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
                Override targets per week for progressive overload, deload phases, or diet breaks.
              </div>
              {!form.duration_weeks ? (
                <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Set a plan duration first (in Plan Builder tab).</div>
              ) : (
                <>
                  <button type="button" className="btn btn-ghost" style={{ marginBottom: 12 }} onClick={initWeeks}>
                    Populate Weeks from Plan Defaults
                  </button>
                  {weeks.map((w, i) => (
                    <div key={i} style={{ background: 'var(--color-surface-2)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Week {w.week_number}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                        {[['Kcal', 'daily_calories'], ['Protein g', 'protein_g'], ['Carbs g', 'carbs_g'], ['Fat g', 'fat_g']].map(([label, key]) => (
                          <div key={key}>
                            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>{label}</div>
                            <input className="form-input" type="number" min="0" value={w[key] || ''}
                              onChange={e => setWeeks(prev => prev.map((wk, j) => j === i ? { ...wk, [key]: e.target.value } : wk))}
                              style={{ minHeight: 36, fontSize: 13 }} />
                          </div>
                        ))}
                      </div>
                      <input className="form-input" placeholder="Notes (e.g. Deload week — reduce intensity)"
                        value={w.notes || ''}
                        onChange={e => setWeeks(prev => prev.map((wk, j) => j === i ? { ...wk, notes: e.target.value } : wk))}
                        style={{ fontSize: 13 }} />
                    </div>
                  ))}
                  {weeks.length > 0 && (
                    <button type="button" className="btn btn-primary" disabled={weeksSaving} onClick={handleSaveWeeks}>
                      {weeksSaving ? 'Saving...' : 'Save Week Plan'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'journal' && (
          <div>
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Client Adherence Journal</div>
              {journal.length === 0 ? (
                <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>No journal entries yet. Clients log progress from their portal.</div>
              ) : (
                journal.map(e => (
                  <div key={e.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600 }}>{e.first_name} {e.last_name}</span>
                      <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{e.journal_date}</span>
                    </div>
                    {e.adherence_score != null && (
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        Adherence: <strong style={{ color: e.adherence_score >= 80 ? 'var(--color-success)' : e.adherence_score >= 50 ? 'var(--color-warning)' : 'var(--color-primary)' }}>{e.adherence_score}%</strong>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {e.actual_calories  != null && <span>{e.actual_calories} kcal eaten</span>}
                      {e.actual_protein_g != null && <span>P: {e.actual_protein_g}g</span>}
                      {e.actual_carbs_g   != null && <span>C: {e.actual_carbs_g}g</span>}
                      {e.actual_fat_g     != null && <span>F: {e.actual_fat_g}g</span>}
                    </div>
                    {e.note && <div style={{ fontSize: 13, marginTop: 4 }}>{e.note}</div>}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
